// === Admin Portal Login ===
// Two-pane dark UI mirroring apps/web/src/pages/login.tsx so the admin portal
// looks consistent with the rest of the product. Email + password sign-in.

import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  CssBaseline,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useAuth } from '../lib/auth';
import { APP_VERSION } from '../version';

const LOGO_URL = 'https://click2gro.com.au/assets/images/Click2gro_white.png';

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
}

function formatDateTime(d: Date): { datePart: string; timePart: string } {
  const formatted = d.toLocaleString('en-AU', {
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatted.split(', ');
  return {
    datePart: parts.slice(0, -1).join(', '),
    timePart: parts.at(-1) ?? '',
  };
}

export function LoginPage() {
  const { signIn, user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(searchParams.get('error'));
  const [submitting, setSubmitting] = useState(false);
  const [now, setNow] = useState(new Date());
  const [isClient, setIsClient] = useState(false);

  useEffect(() => { setIsClient(true); }, []);
  useEffect(() => {
    if (!isClient) return;
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, [isClient]);

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const { datePart, timePart } = isClient ? formatDateTime(now) : { datePart: '', timePart: '' };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setError(null);
    setSubmitting(true);
    const result = await signIn(email, password);
    setSubmitting(false);
    if (result.error) {
      setError(result.error);
    }
  };

  return (
    <>
      <CssBaseline />
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        backgroundColor: '#0a1929',
      }}>
        {/* Left pane — login form */}
        <Box sx={{
          width: { xs: '100%', md: '35%' },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: '#0a1929',
          position: 'relative',
          px: { xs: 3, sm: 5 },
          pt: 2,
          pb: 6,
        }}>
          {/* Logo */}
          <Box sx={{
            position: 'absolute',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
          }}>
            <Box
              component="img"
              src={LOGO_URL}
              alt="Click2Gro"
              sx={{ height: 64, display: 'block' }}
            />
          </Box>

          <Box sx={{
            width: '100%',
            maxWidth: 360,
            mt: 14,
          }}>
            {/* Greeting + clock */}
            {isClient && (
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography sx={{ color: '#ecf0f1', fontSize: '1.75rem', fontWeight: 300, mb: 0.5 }}>
                  {getGreeting()}
                </Typography>
                <Typography sx={{ color: '#1565c0', fontSize: '0.875rem', mb: 0.5 }}>
                  {datePart}
                </Typography>
                <Typography sx={{ color: '#95a5a6', fontSize: '0.75rem' }}>
                  {timePart}
                </Typography>
              </Box>
            )}

            {/* "Admin Portal" badge */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mb: 4,
              p: 1.25,
              border: '1px solid rgba(94, 146, 243, 0.3)',
              borderRadius: 1,
              backgroundColor: 'rgba(21, 101, 192, 0.08)',
            }}>
              <AdminPanelSettingsIcon sx={{ color: '#5e92f3', fontSize: 18 }} />
              <Typography variant="caption" sx={{
                color: '#5e92f3',
                fontSize: '0.7rem',
                letterSpacing: 2,
                textTransform: 'uppercase',
              }}>
                Admin Portal · Superadmin Access
              </Typography>
            </Box>

            {/* Error from query string */}
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  backgroundColor: 'rgba(231, 76, 60, 0.1)',
                  color: '#ecf0f1',
                  border: '1px solid rgba(231, 76, 60, 0.3)',
                  '& .MuiAlert-icon': { color: '#e74c3c' },
                }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={(e: FormEvent) => { void handleSubmit(e); }}>
              <Typography sx={{
                color: '#1565c0',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: 1,
                mb: 1,
                display: 'block',
              }}>
                Email *
              </Typography>
              <TextField
                required
                fullWidth
                placeholder="Enter Email Address"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus={!email}
                variant="outlined"
                sx={{
                  mb: 2.5,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'transparent',
                    borderRadius: 1,
                    '& fieldset': { borderColor: 'rgba(127, 140, 141, 0.5)' },
                    '&:hover fieldset': { borderColor: '#95a5a6' },
                    '&.Mui-focused fieldset': { borderColor: '#1565c0' },
                  },
                  '& .MuiInputBase-input': {
                    color: '#ecf0f1',
                    padding: 1.5,
                  },
                }}
              />

              <Typography sx={{
                color: '#1565c0',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: 1,
                mb: 1,
                display: 'block',
              }}>
                Password *
              </Typography>
              <TextField
                required
                fullWidth
                placeholder="Enter Password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                autoFocus={!!email}
                variant="outlined"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          sx={{ color: '#95a5a6' }}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'transparent',
                    borderRadius: 1,
                    '& fieldset': { borderColor: 'rgba(127, 140, 141, 0.5)' },
                    '&:hover fieldset': { borderColor: '#95a5a6' },
                    '&.Mui-focused fieldset': { borderColor: '#1565c0' },
                  },
                  '& .MuiInputBase-input': {
                    color: '#ecf0f1',
                    padding: 1.5,
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={!email || !password || submitting}
                sx={{
                  backgroundColor: '#1565c0',
                  color: '#ffffff',
                  py: 1.5,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  borderRadius: 1,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#003c8f', boxShadow: 'none' },
                  '&:disabled': { backgroundColor: '#7f8c8d', color: '#ecf0f1', opacity: 0.7 },
                }}
                startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#ecf0f1' }} /> : undefined}
              >
                {submitting ? 'Signing in…' : 'Login'}
              </Button>
            </Box>

            {/* Copyright */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
              <Typography sx={{ color: '#7f8c8d', fontSize: '0.625rem', textAlign: 'center' }}>
                Copyright © Click2Gro 2025 · {APP_VERSION}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right pane — branding */}
        <Box sx={{
          width: '65%',
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: 'linear-gradient(135deg, #0a1929 0%, #1565c0 100%)',
        }}>
          <Box sx={{ textAlign: 'center', color: 'white', p: 4 }}>
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 'light' }}>
              Click2Gro
            </Typography>
            <Typography variant="h2" sx={{ fontWeight: 'normal', mb: 2 }}>
              Admin Portal
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.8, color: '#5e92f3', mb: 4 }}>
              Manage companies, users, and platform settings
            </Typography>
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.5,
              px: 3,
              py: 1.25,
              borderRadius: 999,
              border: '1px solid rgba(255, 255, 255, 0.15)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(8px)',
            }}>
              <AdminPanelSettingsIcon sx={{ color: '#5e92f3', fontSize: 20 }} />
              <Typography variant="caption" sx={{
                color: '#bdc3c7',
                fontSize: '0.75rem',
                letterSpacing: 1.5,
                textTransform: 'uppercase',
              }}>
                Restricted to superadmins
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
